import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  DataTable,
  FilterBar,
  PageHeader,
  Paginacion,
  TarjetasDeEstado,
  ViewToggle,
  type Vista,
} from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta } from "../../components/ui/primitivos";
import { fetchPresupuestos, type PresupuestoListado } from "../../lib/consola-api";
import { fecha } from "../../lib/formato";
import { type Orden, ordenarLista, siguienteOrden } from "../../lib/orden";
import { paginar, paginaValida } from "../../lib/paginacion";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import {
  etiquetaDeEstadoConsulta,
  SinResultados,
  TONO,
  Total,
  VistaLista,
  VistaTarjetas,
} from "./vistas";

/**
 * Presupuestos — porteada de `budgets` de `imprenta` (SPEC 0003 fase 5).
 *
 * El precio de cada renglón se congela al crearlo (ADR 0012). La pantalla lo
 * muestra: un renglón con snapshot dice de dónde salió el importe; uno sin
 * snapshot es un precio escrito a mano, y eso conviene que se vea.
 *
 * De `BudgetList.tsx` se portean las **tres vistas** con su preferencia
 * persistida, las **tarjetas de resumen que además filtran**, la búsqueda, el
 * orden por columna y la columna de renglones. **La edición se agrega acá**: el
 * endpoint existe desde el ADR 0028 y no tenía pantalla.
 */

/**
 * Los cinco filtros de `BudgetList`, en su orden y **con su color**.
 *
 * El color no es decoración: es el mismo del chip de estado de la fila, así que
 * la tarjeta y la tabla se leen con el mismo código (ADR 0094).
 */
const FILTROS = [
  { estado: "PENDIENTE", titulo: "Pendientes", tono: "ambar" },
  { estado: "APROBADO", titulo: "Aprobados", tono: "verde" },
  { estado: "EN_PRODUCCION", titulo: "En producción", tono: "azul" },
  { estado: "RECHAZADO", titulo: "Rechazados", tono: "rojo" },
] as const;

const VISTAS: readonly Vista[] = ["tabla", "tarjetas", "lista"];

/**
 * La vista y el orden son de la PERSONA, no de la sesión — como en el tablero.
 * Los filtros no se guardan a propósito: volver y encontrar un listado filtrado
 * sin recordar por qué es peor que volver a filtrar.
 */
const CLAVE_PREFS = "listado-presupuestos";

interface Preferencias {
  vista: Vista;
  orden: Orden | null;
}

function leerPreferencias(): Preferencias {
  try {
    const guardado = localStorage.getItem(CLAVE_PREFS);
    if (guardado) return { vista: "tabla", orden: null, ...JSON.parse(guardado) };
  } catch {
    // Preferencia corrupta: se arranca con los valores por defecto en vez de
    // romper la pantalla.
  }
  return { vista: "tabla", orden: null };
}

export function PresupuestosPagina() {
  const { can, me } = useSession();
  const navegar = useNavigate();
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [prefs, setPrefs] = useState<Preferencias>(leerPreferencias);
  const [pagina, setPagina] = useState(1);
  const [tamanoPagina, setTamanoPagina] = useState(10);

  useEffect(() => {
    localStorage.setItem(CLAVE_PREFS, JSON.stringify(prefs));
  }, [prefs]);

  // El listado viene entero y el estado se filtra del lado cliente: así las
  // tarjetas pueden mostrar el conteo de CADA estado sin una consulta por
  // tarjeta. El worker sigue aceptando `?estado=` para quien lo necesite.
  const presupuestos = useDatos(() => fetchPresupuestos());

  /**
   * Ver una consulta es NAVEGAR, no montar un modal (ADR 0064).
   *
   * Con una ruta propia la consulta tiene URL: se comparte, se abre en otra
   * pestaña y el botón de atrás vuelve al listado. Nada de eso existía mientras
   * el detalle era estado local de esta pantalla.
   */
  const abrirConsulta = (p: PresupuestoListado) => navegar(`/presupuestos/${p.id}`);

  const todos = presupuestos.datos ?? [];
  const texto = busqueda.trim().toLowerCase();
  const visibles = todos.filter((p) => {
    if (filtroEstado && p.estado !== filtroEstado) return false;
    if (!texto) return true;
    return [p.numero ?? "", p.cliente, p.estado].some((campo) =>
      campo.toLowerCase().includes(texto),
    );
  });

  /**
   * Con `ordenarLista` (ADR 0088) y no con el `sort` propio que había acá.
   *
   * La diferencia aparece justo con la columna nueva: «Válido hasta» es
   * `null` en los presupuestos sin vencimiento, y el `sort` de antes los
   * convertía en `""` — que ordenado asciendente los ponía **primeros**, arriba
   * de todo. Los vacíos van últimos en las dos direcciones: son ausencia de
   * dato, no el dato más chico.
   */
  const ordenados = ordenarLista(visibles, prefs.orden, (p, campo) =>
    campo === "validoHasta" ? p.validoHasta : (p[campo as keyof PresupuestoListado] ?? null),
  );

  const alOrdenar = (campo: string) =>
    setPrefs((p) => ({
      ...p,
      orden: siguienteOrden(p.orden, campo),
    }));

  /**
   * Diez por página, que es el tamaño de `imprenta` para esta pantalla.
   *
   * Va recortada a lo que existe: al filtrar estando en la página 4, quedarse
   * ahí muestra una tabla vacía con el paginador diciendo que hay filas. Es
   * `paginaValida` y no un `useEffect` que resetee — un efecto que corrige
   * estado en respuesta a otro estado es un render de más y un lugar donde el
   * bug vuelve.
   */
  const paginaActual = paginaValida(pagina, ordenados.length, tamanoPagina);
  const enPantalla = paginar(ordenados, paginaActual, tamanoPagina);

  /**
   * Si esta persona puede editarlo (ADR 0028): sin `presupuestos.update_all`,
   * solo lo propio. **Ocultar no es autorizar** —el worker contesta 403 igual—,
   * pero ofrecer un botón que va a fallar es peor que no ofrecerlo.
   */
  const puedeEditar = (p: PresupuestoListado) =>
    can("presupuestos", "update") &&
    (can("presupuestos", "update_all") || p.creadoPorId === me?.id);

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Consultas"
        subtitulo="Cotizaciones y su paso a producción"
        acciones={
          <>
            <ViewToggle
              valor={prefs.vista}
              opciones={VISTAS}
              alCambiar={(vista) => setPrefs((p) => ({ ...p, vista }))}
            />
            {can("presupuestos", "create") ? (
              // El alta es una PANTALLA, no un modal: los ítems se editan en su
              // lugar y el modal no daba lugar para eso (ADR 0055).
              <Boton onClick={() => navegar("/presupuestos/nueva")}>Nueva Consulta</Boton>
            ) : null}
          </>
        }
      />

      {presupuestos.error ? <Aviso>{presupuestos.error}</Aviso> : null}

      {/* La familia «estado» y no `StatsCards` (ADR 0094): acá las cinco son
          el filtro, y el fondo de color es lo que lo dice. */}
      <TarjetasDeEstado
        tarjetas={[
          {
            clave: "todos",
            titulo: "Todos",
            valor: todos.length,
            tono: "neutro",
            activa: filtroEstado === "",
            alClickear: () => setFiltroEstado(""),
          },
          ...FILTROS.map((f) => ({
            clave: f.estado,
            titulo: f.titulo,
            valor: todos.filter((p) => p.estado === f.estado).length,
            tono: f.tono,
            activa: filtroEstado === f.estado,
            alClickear: () => setFiltroEstado(filtroEstado === f.estado ? "" : f.estado),
          })),
        ]}
      />

      <FilterBar
        filtrosActivos={(filtroEstado ? 1 : 0) + (texto ? 1 : 0)}
        alLimpiar={() => {
          setFiltroEstado("");
          setBusqueda("");
        }}
        busqueda={
          <input
            aria-label="Buscar"
            placeholder="Buscar por número, cliente o estado..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        }
      >
        <select
          aria-label="Estado"
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="rounded-md border border-neutral-300 bg-white px-2 py-1.5 text-neutral-900 text-sm"
        >
          <option value="">Todos los estados</option>
          {Object.keys(TONO).map((e) => (
            <option key={e} value={e}>
              {etiquetaDeEstadoConsulta(e)}
            </option>
          ))}
        </select>
      </FilterBar>

      {presupuestos.cargando && todos.length === 0 ? (
        <SinResultados mensaje="Cargando..." />
      ) : ordenados.length === 0 ? (
        <SinResultados
          mensaje={todos.length === 0 ? "No hay presupuestos" : "Nada coincide con ese filtro"}
        />
      ) : prefs.vista === "tarjetas" ? (
        <VistaTarjetas presupuestos={ordenados} alAbrir={abrirConsulta} />
      ) : prefs.vista === "lista" ? (
        <VistaLista presupuestos={ordenados} alAbrir={abrirConsulta} />
      ) : (
        <DataTable
          datos={enPantalla}
          clave={(p) => p.id}
          cargando={false}
          mensajeVacio="No hay presupuestos"
          orden={prefs.orden}
          alOrdenar={alOrdenar}
          columnas={[
            {
              clave: "numero",
              ordenarPor: "numero",
              encabezado: "Número",
              render: (p) => <span className="font-mono text-sm">{p.numero ?? "—"}</span>,
            },
            {
              clave: "cliente",
              ordenarPor: "cliente",
              encabezado: "Cliente",
              render: (p) => p.cliente,
            },
            {
              clave: "items",
              encabezado: "Renglones",
              className: "text-neutral-600",
              render: (p) => p.items,
            },
            // Las dos columnas de `imprenta` que faltaban, y en su orden: entre
            // los renglones y el total.
            {
              clave: "validoHasta",
              ordenarPor: "validoHasta",
              encabezado: "Válido hasta",
              className: "text-neutral-600",
              render: (p) => fecha(p.validoHasta),
            },
            {
              clave: "formaPago",
              encabezado: "Forma de pago",
              className: "text-neutral-600",
              // `null` cuando no se eligió ninguna, que es un estado normal del
              // presupuesto — no un dato que falta por error.
              render: (p) => p.formaPago ?? "—",
            },
            {
              clave: "total",
              encabezado: "Total",
              // Sin precio en algún renglón, el total sería mentira: va vacío.
              render: (p) => <Total monto={p.montoTotal} />,
            },
            {
              clave: "estado",
              ordenarPor: "estado",
              encabezado: "Estado",
              render: (p) => (
                <Etiqueta tono={TONO[p.estado] ?? "neutral"}>
                  {etiquetaDeEstadoConsulta(p.estado)}
                </Etiqueta>
              ),
            },
            {
              clave: "acciones",
              encabezado: "",
              className: "text-right",
              render: (p) => (
                <div className="flex justify-end gap-1">
                  {p.estado === "BORRADOR" && puedeEditar(p) ? (
                    <Boton
                      variante="fantasma"
                      onClick={() => navegar(`/presupuestos/${p.id}/editar`)}
                    >
                      Editar
                    </Boton>
                  ) : null}
                  <Boton variante="fantasma" onClick={() => abrirConsulta(p)}>
                    Ver
                  </Boton>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* Solo en la vista tabla, como `imprenta`: las tarjetas y la lista
          muestran todo. Y `ordenados.length`, no `todos.length` — el total del
          paginador es el del conjunto FILTRADO, si no dice que hay 141 filas
          mientras se ven 3. */}
      {prefs.vista === "tabla" && ordenados.length > 0 ? (
        <Paginacion
          pagina={paginaActual}
          tamano={tamanoPagina}
          total={ordenados.length}
          alCambiarPagina={setPagina}
          alCambiarTamano={(t) => {
            setTamanoPagina(t);
            setPagina(1);
          }}
        />
      ) : null}
    </div>
  );
}
